import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { RecommendationGroup, Quotation, UnifiedItem } from './types';
import { Button } from '@/components/ui/button';
import { GripVertical, Trash, Building, Map, Car, Utensils, Box, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LibraryBrowser } from './LibraryBrowser';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { formatSafeDate } from '@/lib/date-utils';
import { createGoogleMapsUrl, openInGoogleMaps } from '@/lib/google-maps-utils';
import { ExternalLink } from 'lucide-react';

interface RecommendationItemsProps {
  group: RecommendationGroup;
  quotation: Quotation;
  handleChange: (field: keyof Quotation, value: any) => void;
}

export function RecommendationItems({ group, quotation, handleChange }: RecommendationItemsProps) {
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [currentItemType, setCurrentItemType] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<UnifiedItem | null>(null);

  // Ensure group and itemIds are properly initialized
  useEffect(() => {
    if (!group.itemIds) {
      // This is a defensive fix to ensure itemIds is always an array
      const updatedGroups = (quotation.recommendationGroups || []).map(g =>
        g.id === group.id ? { ...g, itemIds: [] } : g
      );
      
      handleChange('recommendationGroups', updatedGroups);
    }
    
    if (!quotation.recommendations) {
      handleChange('recommendations', []);
    }
  }, [group, quotation, handleChange]);

  // Update the items assignment with defensive code
  const items = (group.itemIds || [])
    .map(id => quotation.recommendations?.find(item => item.id === id))
    .filter((item): item is UnifiedItem => !!item);

  const handleRemoveItem = (itemId: string) => {
    const updatedGroup = {
      ...group,
      itemIds: group.itemIds.filter(id => id !== itemId)
    };
    
    const updatedGroups = (quotation.recommendationGroups || []).map(g =>
      g.id === group.id ? updatedGroup : g
    );
    
    handleChange('recommendationGroups', updatedGroups);
  };

  const handleAddItem = (item: UnifiedItem) => {
    // Adicionar o item à lista de recomendações se ainda não existir
    if (!quotation.recommendations?.find(r => r.id === item.id)) {
      handleChange('recommendations', [
        ...(quotation.recommendations || []),
        item
      ]);
    }

    // Adicionar o ID do item ao grupo
    const updatedGroup = {
      ...group,
      itemIds: [...group.itemIds, item.id]
    };

    const updatedGroups = (quotation.recommendationGroups || []).map(g =>
      g.id === group.id ? updatedGroup : g
    );

    handleChange('recommendationGroups', updatedGroups);
  };

  // Added basic onDragEnd handler
  const onDragEnd = (result) => {
    // Basic implementation - no item reordering for now
    // This is just to satisfy the DragDropContext requirement
    if (!result.destination) return;
    // We could implement item reordering here if needed
  };

  // Função para renderizar o conteúdo detalhado do item no modal
  const renderItemDetails = (item: UnifiedItem) => {
    switch (item.type) {
      case 'block':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{item.block?.title}</h3>
            {item.block?.images && item.block.images.length > 0 && (
              <Carousel className="w-full">
                <CarouselContent>
                  {item.block.images.map((image, idx) => (
                    <CarouselItem key={idx}>
                      <div className="aspect-video w-full overflow-hidden rounded-lg">
                        <img src={image} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            )}
            {item.block?.description && (
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: item.block.description }} />
              </div>
            )}
            {(() => {
              console.log('[RecommendationItems] Rendering details for Block:', item.block);
              const blockMapUrl = createGoogleMapsUrl(item.block);
              console.log('[RecommendationItems] Google Maps URL for Block:', blockMapUrl);
              if (blockMapUrl) {
                return (
                  <Button variant="outline" size="sm" onClick={() => openInGoogleMaps(item.block)} className="mt-2">
                    <ExternalLink className="h-4 w-4 mr-2" /> Ver no Google Maps
                  </Button>
                );
              }
              return null;
            })()}
          </div>
        );

      case 'tour':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{item.tour?.name}</h3>
            {item.tour?.images && item.tour.images.length > 0 && (
              <Carousel className="w-full">
                <CarouselContent>
                  {item.tour.images.map((image, idx) => (
                    <CarouselItem key={idx}>
                      <div className="aspect-video w-full overflow-hidden rounded-lg">
                        <img src={image} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            )}
            {item.tour?.description && (
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: item.tour.description }} />
              </div>
            )}
            {item.tour?.location && (
              <div className="text-sm text-gray-500">
                <strong>Localização:</strong> {item.tour.location}
              </div>
            )}
            {(() => {
              console.log('[RecommendationItems] Rendering details for Tour:', item.tour);
              const tourMapUrl = createGoogleMapsUrl(item.tour);
              console.log('[RecommendationItems] Google Maps URL for Tour:', tourMapUrl);
              if (tourMapUrl) {
                return (
                  <Button variant="outline" size="sm" onClick={() => openInGoogleMaps(item.tour)} className="mt-2">
                    <ExternalLink className="h-4 w-4 mr-2" /> Ver no Google Maps
                  </Button>
                );
              }
              return null;
            })()}
          </div>
        );

      case 'accommodation':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{item.accommodation?.name}</h3>
            {item.accommodation?.images && item.accommodation.images.length > 0 && (
              <Carousel className="w-full">
                <CarouselContent>
                  {item.accommodation.images.map((image, idx) => (
                    <CarouselItem key={idx}>
                      <div className="aspect-video w-full overflow-hidden rounded-lg">
                        <img src={image} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            )}
            {item.accommodation?.description && (
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: item.accommodation.description }} />
              </div>
            )}
            {item.accommodation?.location && (
              <div className="text-sm text-gray-500">
                <strong>Localização:</strong> {item.accommodation.location}
              </div>
            )}
            {item.accommodation?.roomType && (
              <div className="text-sm text-gray-500">
                <strong>Tipo de Quarto:</strong> {item.accommodation.roomType}
              </div>
            )}
            {(() => {
              console.log('[RecommendationItems] Rendering details for Accommodation:', item.accommodation);
              const accommodationMapUrl = createGoogleMapsUrl(item.accommodation);
              console.log('[RecommendationItems] Google Maps URL for Accommodation:', accommodationMapUrl);
              if (accommodationMapUrl) {
                return (
                  <Button variant="outline" size="sm" onClick={() => openInGoogleMaps(item.accommodation)} className="mt-2">
                    <ExternalLink className="h-4 w-4 mr-2" /> Ver no Google Maps
                  </Button>
                );
              }
              return null;
            })()}
          </div>
        );

      case 'transportation':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{item.transportation?.name}</h3>
            {item.transportation?.images && item.transportation.images.length > 0 && (
              <Carousel className="w-full">
                <CarouselContent>
                  {item.transportation.images.map((image, idx) => (
                    <CarouselItem key={idx}>
                      <div className="aspect-video w-full overflow-hidden rounded-lg">
                        <img src={image} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            )}
            {item.transportation?.description && (
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: item.transportation.description }} />
              </div>
            )}
            {item.transportation?.departureLocation && item.transportation?.arrivalLocation && (
              <div className="text-sm text-gray-500">
                <strong>De:</strong> {item.transportation.departureLocation}<br />
                <strong>Para:</strong> {item.transportation.arrivalLocation}
              </div>
            )}
          </div>
        );

      case 'restaurant':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{item.restaurant?.name}</h3>
            {item.restaurant?.images && item.restaurant.images.length > 0 && (
              <Carousel className="w-full">
                <CarouselContent>
                  {item.restaurant.images.map((image, idx) => (
                    <CarouselItem key={idx}>
                      <div className="aspect-video w-full overflow-hidden rounded-lg">
                        <img src={image} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            )}
            {item.restaurant?.description && (
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: item.restaurant.description }} />
              </div>
            )}
            {item.restaurant?.location && (
              <div className="text-sm text-gray-500">
                <strong>Localização:</strong> {item.restaurant.location}
              </div>
            )}
            {item.restaurant?.cuisineType && (
              <div className="text-sm text-gray-500">
                <strong>Tipo de Cozinha:</strong> {item.restaurant.cuisineType}
              </div>
            )}
            {(() => {
              console.log('[RecommendationItems] Rendering details for Restaurant:', item.restaurant);
              const restaurantMapUrl = createGoogleMapsUrl(item.restaurant);
              console.log('[RecommendationItems] Google Maps URL for Restaurant:', restaurantMapUrl);
              if (restaurantMapUrl) {
                return (
                  <Button variant="outline" size="sm" onClick={() => openInGoogleMaps(item.restaurant)} className="mt-2">
                    <ExternalLink className="h-4 w-4 mr-2" /> Ver no Google Maps
                  </Button>
                );
              }
              return null;
            })()}
          </div>
        );

      default:
        return <div>Detalhes não disponíveis</div>;
    }
  };

  // Função para renderizar o ícone e nome do item baseado no tipo
  const renderItemContent = (item: UnifiedItem) => {
    switch (item.type) {
      case 'block':
        return (
          <div className="flex items-center">
            <Box className="h-4 w-4 mr-2 text-amber-500" />
            <span>{item.block?.title || 'Bloco'}</span>
          </div>
        );
      case 'tour':
        return (
          <div className="flex items-center">
            <Map className="h-4 w-4 mr-2 text-amber-500" />
            <span>{item.tour?.name || 'Passeio'}</span>
          </div>
        );
      case 'accommodation':
        return (
          <div className="flex items-center">
            <Building className="h-4 w-4 mr-2 text-green-500" />
            <span>{item.accommodation?.name || 'Hospedagem'}</span>
          </div>
        );
      case 'transportation':
        return (
          <div className="flex items-center">
            <Car className="h-4 w-4 mr-2 text-blue-500" />
            <span>{item.transportation?.name || 'Transporte'}</span>
          </div>
        );
      case 'restaurant':
        return (
          <div className="flex items-center">
            <Utensils className="h-4 w-4 mr-2 text-purple-500" />
            <span>{item.restaurant?.name || 'Restaurante'}</span>
          </div>
        );
      default:
        return <span>Item</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Searchbar que abre o LibraryBrowser */}
      <div className="relative">
        <Input
          type="text"
          placeholder="Buscar item para adicionar..."
          className="w-full pl-10"
          onClick={() => setIsLibraryOpen(true)}
          readOnly
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={group.id} type="item">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-2"
            >
              {items.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn(
                        "flex items-center p-2 rounded-md border bg-white",
                        snapshot.isDragging && "shadow-lg"
                      )}
                    >
                      <div
                        {...provided.dragHandleProps}
                        className="mr-2"
                      >
                        <GripVertical className="h-4 w-4 text-gray-400" />
                      </div>
                      
                      <div className="flex-1">
                        {renderItemContent(item)}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedItem(item)}
                          className="text-voah-teal hover:text-voah-teal-dark"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <LibraryBrowser
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelectItem={(libraryItem) => {
          // Log the received library item
          console.log('[RecommendationItems] Library item received:', JSON.stringify(libraryItem, null, 2)); 

          // Converter o item da biblioteca para UnifiedItem
          // Start with the base data
          let unifiedItemData: any = { ...(libraryItem.data || {}) }; 

          // Merge location data directly into the item data if location exists
          if (libraryItem.location) {
            console.log('[RecommendationItems] Merging location data:', libraryItem.location);
            unifiedItemData = {
              ...unifiedItemData, // Keep existing data
              placeId: libraryItem.location.placeId, // Add/overwrite placeId
              coordinates: libraryItem.location.coordinates, // Add/overwrite coordinates
              // Optionally merge address/location string if needed, prioritizing formattedAddress
              address: libraryItem.location.formattedAddress || unifiedItemData.address, 
              location: libraryItem.location.formattedAddress || unifiedItemData.location 
            };
          } else {
             console.log('[RecommendationItems] No location data found in library item.');
          }

          // Log the final data object being assigned
          console.log('[RecommendationItems] Final unifiedItemData for type', libraryItem.type, ':', JSON.stringify(unifiedItemData, null, 2));

          const unifiedItem: UnifiedItem = {
            id: libraryItem.id,
            type: libraryItem.type,
            globalOrder: items.length, // Consider if globalOrder needs adjustment based on actual position
            [libraryItem.type]: unifiedItemData // Assign the merged data object
          };
          
          // Log the final UnifiedItem
          console.log('[RecommendationItems] Creating UnifiedItem:', JSON.stringify(unifiedItem, null, 2));

          handleAddItem(unifiedItem);
          setIsLibraryOpen(false);
        }}
        itemType={currentItemType}
      />

      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Item</DialogTitle>
          </DialogHeader>
          {selectedItem && renderItemDetails(selectedItem)}
        </DialogContent>
      </Dialog>
    </div>
  );
} 